import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BellIcon, XIcon } from 'lucide-react';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  EmptyState,
  Spinner,
} from '@noor/ui';
import { useVendorBroadcasts } from '@/entities/vendor-broadcast';
import { useMarkBroadcastsViewed } from '../model/use-mark-broadcasts-viewed';
import { BroadcastCard } from './broadcast-card';

/**
 * The full notification history — the old panel's `BroadcastDrawer`, ported.
 * Opening it marks every fresh item viewed (once per opening); the badge
 * count drops on the invalidation that follows.
 */
export function NotificationsDrawer({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation('common');
  const { data: items = [], isLoading } = useVendorBroadcasts();
  const { mutate: markViewed } = useMarkBroadcastsViewed();
  const markingRef = useRef(false);

  useEffect(() => {
    if (!open) {
      markingRef.current = false;
      return;
    }
    if (markingRef.current) return;
    const freshIds = items
      .filter((item) => item.status === 'fresh')
      .map((item) => item.id);
    if (freshIds.length > 0) {
      markingRef.current = true;
      markViewed(freshIds);
    }
  }, [open, items, markViewed]);

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      direction="right"
    >
      <DrawerContent
        // Full-bleed on phones, the source's 500px on desktop. Same
        // data-variant prefix as the base so tailwind-merge overrides it.
        className="data-[vaul-drawer-direction=right]:w-full data-[vaul-drawer-direction=right]:sm:max-w-[500px]"
      >
        <DrawerHeader className="flex-row items-center justify-between gap-4 p-4 text-left sm:p-6 sm:pb-4">
          <DrawerTitle className="text-[22px] font-semibold tracking-tight">
            {t('broadcasts.notifications')}
          </DrawerTitle>
          <DrawerClose asChild>
            <button
              type="button"
              onClick={onClose}
              aria-label={t('broadcasts.close')}
              className="cursor-pointer rounded-full p-1 text-muted-foreground transition-colors outline-none hover:bg-muted hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/40"
            >
              <XIcon className="size-6" />
            </button>
          </DrawerClose>
        </DrawerHeader>
        <div className="flex-1 overflow-y-auto p-4 pt-2 sm:p-6 sm:pt-2">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : items.length === 0 ? (
            <EmptyState icon={BellIcon} title={t('broadcasts.no_notifications')} />
          ) : (
            <div className="flex flex-col gap-3">
              {items.map((item) => (
                <BroadcastCard key={item.id} item={item} />
              ))}
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
